"use client";

import { HTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/utils";

type BadgeVariant =
  | "default"
  | "success"
  | "warning"
  | "error"
  | "info"
  | "primary";
type BadgeSize = "sm" | "md";

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  size?: BadgeSize;
  children: ReactNode;
}

const variantStyles: Record<BadgeVariant, string> = {
  default: "bg-card-hover text-foreground",
  success: "bg-success-glow-12 text-success-light",
  warning: "bg-warning-glow-12 text-warning-light",
  error: "bg-error-glow-12 text-error-light",
  info: "bg-info-glow-12 text-info-light",
  primary: "bg-brand-glow-12 text-brand-light",
};

const sizeStyles: Record<BadgeSize, string> = {
  sm: "px-[9px] py-[3px] text-[11px]",
  md: "px-2.5 py-1 text-[11px]",
};

function Badge({
  variant = "default",
  size = "md",
  children,
  className = "",
  ...props
}: BadgeProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center justify-center gap-1.5 font-mono font-medium tracking-[0.06em]",
        "rounded-full whitespace-nowrap",
        variantStyles[variant],
        sizeStyles[size],
        className,
      )}
      {...props}
    >
      {children}
    </span>
  );
}

export { Badge, type BadgeProps, type BadgeVariant };
